import { useParams, Navigate, Link } from 'react-router-dom'
import { getJourneyBySlug } from '@/data/journeys'
import type { TimelineStep, JourneyFeature } from '@/data/journeys'
import { Button } from '@/components/ui'
import { SEO } from '@/components/seo'
import JourneyIntro from '@/components/JourneyIntro'

// Icon component for timeline and features
function Icon({
  name,
  color,
  className,
}: {
  name: string
  color?: string
  className?: string
}) {
  const colorClasses: Record<string, string> = {
    teal: 'bg-accent-teal/15 text-accent-teal',
    violet: 'bg-violet-500/15 text-violet-400',
    amber: 'bg-star/15 text-star',
    emerald: 'bg-emerald-500/15 text-emerald-400',
    rose: 'bg-rose-500/15 text-rose-400',
    pink: 'bg-pink-500/15 text-pink-400',
    indigo: 'bg-indigo-500/15 text-indigo-400',
    sky: 'bg-sky-500/15 text-sky-400',
  }

  const icons: Record<string, React.ReactNode> = {
    video: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    ),
    document: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    clipboard: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
      </svg>
    ),
    search: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    ),
    sparkle: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
      </svg>
    ),
    mic: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z"
        />
      </svg>
    ),
    shield: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
        />
      </svg>
    ),
    chart: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
      </svg>
    ),
    users: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    clock: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    ),
    bolt: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    check: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  }

  return (
    <div
      className={`${color ? colorClasses[color] || colorClasses.teal : ''} ${className || ''}`}
    >
      {icons[name] || icons.document}
    </div>
  )
}

// Timeline step component
function TimelineStepCard({ step, index }: { step: TimelineStep; index: number }) {
  return (
    <div className="relative pl-[88px] pb-16 last:pb-0">
      {/* Marker */}
      <div className="absolute left-4 top-0 w-[34px] h-[34px] bg-gradient-to-br from-accent-teal to-emerald-500 rounded-full flex items-center justify-center text-brand-dark font-bold text-sm shadow-[0_0_0_6px_#0f1318,0_0_20px_rgba(20,184,166,0.3)]">
        {index + 1}
      </div>

      {/* Time */}
      <div className="text-[13px] font-semibold text-accent-teal tracking-[0.05em] mb-2">
        {step.time}
      </div>

      {/* Card */}
      <div className="bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-white/[0.06] rounded-2xl py-7 px-8 transition-all duration-400 hover:border-accent-teal/20 hover:translate-x-2 hover:shadow-[0_20px_40px_rgba(0,0,0,0.3)]">
        <div className="flex items-center gap-4 mb-4">
          <Icon
            name={step.icon}
            color={step.iconColor}
            className="w-12 h-12 rounded-xl flex items-center justify-center [&>svg]:w-6 [&>svg]:h-6"
          />
          <div>
            <h3 className="font-serif text-[22px] font-medium mb-1">{step.title}</h3>
            <p className="text-[13px] text-brand-subtle">{step.meta}</p>
          </div>
        </div>
        <p className="text-[15px] text-brand-muted leading-[1.7] mb-5">{step.body}</p>
        <div className="bg-black/30 border border-white/[0.06] rounded-lg p-4">
          <p className="text-[10px] font-semibold text-accent-teal tracking-[0.1em] uppercase mb-2">
            {step.previewLabel}
          </p>
          <p className="text-[13px] text-brand-muted leading-[1.6]">{step.previewText}</p>
        </div>
      </div>
    </div>
  )
}

// Feature card component
function FeatureCard({ feature }: { feature: JourneyFeature }) {
  return (
    <div className="bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-white/[0.06] rounded-2xl py-10 px-8 transition-all duration-400 hover:-translate-y-1 hover:border-accent-teal/20 hover:shadow-[0_20px_40px_rgba(0,0,0,0.3)] relative overflow-hidden group">
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-accent-teal to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
      <Icon
        name={feature.icon}
        color="teal"
        className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 [&>svg]:w-8 [&>svg]:h-8"
      />
      <h3 className="font-serif text-[22px] font-medium tracking-[-0.01em] mb-3">
        {feature.title}
      </h3>
      <p className="text-[15px] text-brand-muted leading-[1.7]">{feature.body}</p>
    </div>
  )
}

export default function JourneyPage() {
  const { slug } = useParams<{ slug: string }>()
  const journey = slug ? getJourneyBySlug(slug) : undefined

  // If journey doesn't exist, redirect to home
  if (!journey) {
    return <Navigate to="/" replace />
  }

  return (
    <>
      <JourneyIntro sentences={journey.intro} journeySlug={journey.slug} />
      <SEO
        title={journey.seo.title}
        description={journey.seo.description}
        keywords={journey.seo.keywords}
        url={`/journey/${journey.slug}`}
      />

      {/* Hero */}
      <section className="py-[100px] px-6 max-w-[1200px] mx-auto text-center relative z-[1]">
        <p className="text-[14px] font-semibold text-accent-teal tracking-[0.1em] uppercase mb-6">
          {journey.hero.eyebrow}
        </p>
        <h1 className="font-serif text-[clamp(42px,6vw,64px)] font-normal leading-[1.15] tracking-[-0.02em] mb-6 max-w-[900px] mx-auto">
          {journey.hero.title}
          <br />
          <span className="text-accent-teal">{journey.hero.titleAccent}</span>
        </h1>
        <p className="text-[19px] text-brand-muted leading-[1.7] max-w-[600px] mx-auto mb-10">
          {journey.hero.subtitle}
        </p>
        <div className="flex gap-4 justify-center mb-4">
          <Button href="/get-started" size="lg">
            Get Started
            <svg
              className="w-[18px] h-[18px]"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Button>
          <Button variant="secondary" size="lg">
            <svg
              className="w-[18px] h-[18px]"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <polygon points="10 8 16 12 10 16 10 8" fill="currentColor" />
            </svg>
            Watch it happen
          </Button>
        </div>
        <p className="text-sm text-brand-subtle">
          {journey.hero.microcopy}
        </p>
      </section>

      {/* Journey Timeline */}
      <section className="py-[120px] px-6 relative z-[1]">
        <div className="max-w-[1000px] mx-auto">
          <div className="text-center mb-20">
            <p className="text-xs font-semibold text-accent-teal tracking-[0.15em] uppercase mb-4">
              {journey.journeySection.label}
            </p>
            <h2 className="font-serif text-[clamp(32px,4vw,42px)] font-normal tracking-[-0.02em] mb-4">
              {journey.journeySection.title}{' '}
              <span className="text-accent-teal">{journey.journeySection.titleAccent}</span>
            </h2>
            <p className="text-lg text-brand-muted max-w-[500px] mx-auto">
              {journey.journeySection.subtitle}
            </p>
          </div>

          {/* Timeline */}
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-accent-teal to-accent-teal/20" />

            {/* Steps */}
            {journey.timeline.map((step, index) => (
              <TimelineStepCard key={index} step={step} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Result */}
      <section className="py-10 px-6 pb-[120px] relative z-[1]">
        <div className="max-w-[900px] mx-auto bg-gradient-to-br from-accent-teal/[0.08] to-accent-teal/[0.02] border border-accent-teal/20 rounded-3xl p-12 text-center">
          <div className="w-[72px] h-[72px] bg-gradient-to-br from-accent-teal to-emerald-500 rounded-[20px] flex items-center justify-center mx-auto mb-6 text-brand-dark">
            <svg
              className="w-9 h-9"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="font-serif text-[32px] font-normal mb-4">
            {journey.result.title} <span className="text-accent-teal">{journey.result.titleAccent}</span>
          </h2>
          <p className="text-lg text-brand-muted leading-[1.7] max-w-[550px] mx-auto mb-8">
            {journey.result.body}
          </p>
          <div className="flex justify-center gap-12 pt-8 border-t border-white/10">
            {journey.result.stats.map((stat, index) => (
              <div key={index}>
                <div className="font-serif text-[42px] font-light text-accent-teal">
                  {stat.value}
                </div>
                <div className="text-sm text-brand-subtle">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-[120px] px-6 bg-black/20 relative z-[1]">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-20">
            <p className="text-xs font-semibold text-accent-teal tracking-[0.15em] uppercase mb-5">
              {journey.features.label}
            </p>
            <h2 className="font-serif text-[48px] font-normal tracking-[-0.02em] mb-5">
              {journey.features.title}{' '}
              <span className="text-accent-teal">{journey.features.titleAccent}</span>
            </h2>
            <p className="text-lg text-brand-muted max-w-[600px] mx-auto">
              {journey.features.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {journey.features.items.map((feature, index) => (
              <FeatureCard key={index} feature={feature} />
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-[120px] px-6 relative z-[1]">
        <div className="max-w-[800px] mx-auto bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-white/[0.06] rounded-2xl p-10 text-center transition-all duration-400 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(0,0,0,0.3)]">
          <div className="font-serif text-[80px] text-accent-teal/20 leading-[0.5] mb-6">"</div>
          <p className="text-[17px] text-brand-light leading-[1.8] mb-8">{journey.testimonial.quote}</p>
          <div className="flex items-center justify-center gap-4 pt-6 border-t border-white/[0.06]">
            <div className="w-12 h-12 bg-gradient-to-br from-accent-teal to-emerald-500 rounded-xl flex items-center justify-center text-sm font-semibold text-brand-dark">
              {journey.testimonial.initials}
            </div>
            <div className="text-left">
              <div className="text-[15px] font-semibold text-brand-light">
                {journey.testimonial.authorName}
              </div>
              <div className="text-[13px] text-brand-muted">{journey.testimonial.authorRole}</div>
              {journey.testimonial.authorLocation && (
                <div className="text-xs text-brand-subtle">{journey.testimonial.authorLocation}</div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 pb-[120px] relative z-[1]">
        <div className="max-w-[900px] mx-auto bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-white/[0.08] rounded-3xl py-20 px-12 text-center">
          <h2 className="font-serif text-[44px] font-normal tracking-[-0.02em] mb-5">
            {journey.cta.title} <span className="text-accent-teal">{journey.cta.titleAccent}</span> next meeting
          </h2>
          <p className="text-lg text-brand-muted leading-[1.7] max-w-[500px] mx-auto mb-10">
            {journey.cta.subtitle}
          </p>
          <div className="flex justify-center gap-4 mb-6">
            <Button href="/get-started" size="lg">
              Get Started
              <svg
                className="w-[18px] h-[18px]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </Button>
            <Button href="/pricing" variant="secondary" size="lg">
              View Pricing
            </Button>
          </div>
          <p className="text-sm text-brand-subtle">{journey.cta.note}</p>
        </div>
      </section>

      {/* Back to Home */}
      <div className="text-center px-6 pb-16 relative z-[1]">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-brand-subtle hover:text-brand-muted transition-colors"
        >
          <svg
            className="w-4 h-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Explore more journeys
        </Link>
      </div>
    </>
  )
}
